import { watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUserStore } from '../store/userStore';


/* 允許的介面語言
   - code → i18n語言代碼
   - label → UI顯示名稱
*/
const supportedLanguages = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'zh', label: '中文' },
];

const languageCodeToDisplay: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  zh: 'Simplified_Chinese',
};

const displayToCode: Record<string, string> = Object.fromEntries(
  Object.entries(languageCodeToDisplay).map(([code, display]) => [display, code])
);

/* 只有 Admin 或 @mimir.com 帳號能使用非英文介面。
   這僅限制 i18n 介面文案，不影響 siteLanguage（使用者母語，字典查詢用）。 */
function canUseLocalizedUI(): boolean {
  const userStore = useUserStore();
  return (
    userStore.user_role === 'Admin' ||
    (userStore.email?.trim().toLowerCase().endsWith('@mimir.com') ?? false)
  );
}

/* 由母語推出介面語言代碼，無權限或語言不支援時一律英文 */
export function resolveUiLocale(siteLanguage: string | null): string {
  if (!canUseLocalizedUI()) return 'en';

  const code = displayToCode[siteLanguage || ''];
  return supportedLanguages.find(lang => lang.code === code) ? code : 'en';
}

export function useLanguage() {
  const { t, locale } = useI18n();

  // const switchLanguage = () => {
  //   const currentIndex = supportedLanguages.findIndex(lang => lang.code === locale.value);
  //   const nextIndex = (currentIndex + 1) % supportedLanguages.length;
  //   setLanguage(supportedLanguages[nextIndex].code);
  // };

  /* 切換 i18n 語言 */
  const setLanguage = (code: string) => {
    // 檢查是否為支援語言
    if (!supportedLanguages.find(lang => lang.code === code)) {
      console.warn('Unknown language code:', code);
      return;
    }

    locale.value = canUseLocalizedUI() ? code : 'en';
  };

  // 載入時還原使用者介面語言。
  // user_role / email 由 /user/me 非同步填入，首次同步呼叫時還是空的，
  // 而 /user、/teacher、各 room 路由不在 DefaultLayout 底下（沒有 MainHeader
  // 重新套用），所以 hydrate 後要自己重判一次。
  const initLanguage = () => {
    const userStore = useUserStore();

    const apply = () => {
      locale.value = resolveUiLocale(
        userStore.site_language || localStorage.getItem('siteLanguage')
      );
    };

    apply();
    watch(
      () => [userStore.user_role, userStore.email, userStore.site_language],
      apply
    );
  };

  return {
    t,                 // vue-i18n 翻譯函式
    locale,            // 當前語言 (ref)
    // switchLanguage,    // 循環切換語言
    setLanguage,       // 指定語言
    supportedLanguages,// 語言清單（給 UI 用）
    initLanguage,      // 初始化語言（App 啟動時呼叫）
  };
}
